import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "../helpers/class-names";
import { useTheme } from "./ThemeProvider";
import type { ThemePreference } from "./theme-preference";
import { ActionIcon } from "../components/ActionIcon";

export function ThemeSelect({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  return (
    <label className={cn("ui-theme-field", className)}>
      Theme
      <select
        aria-label="Theme"
        value={theme}
        onChange={(event) => setTheme(event.target.value as ThemePreference)}
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  );
}

export function ThemeIconSwitcher({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: "system" as const, label: "Use system theme", icon: Monitor },
    { value: "light" as const, label: "Use light theme", icon: Sun },
    { value: "dark" as const, label: "Use dark theme", icon: Moon },
  ];
  return (
    <div
      className={cn("ui-theme-switcher", className)}
      role="group"
      aria-label="Theme"
    >
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <ActionIcon
            aria-label={option.label}
            aria-pressed={theme === option.value}
            icon={<Icon />}
            label={option.label}
            onClick={() => setTheme(option.value)}
            key={option.value}
            size="small"
          />
        );
      })}
    </div>
  );
}

export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const options = {
    system: {
      next: "light" as const,
      label: "System theme. Switch to light theme",
      icon: Monitor,
    },
    light: {
      next: "dark" as const,
      label: "Light theme. Switch to dark theme",
      icon: Sun,
    },
    dark: {
      next: "system" as const,
      label: "Dark theme. Switch to system theme",
      icon: Moon,
    },
  };
  const option = options[theme];
  const Icon = option.icon;
  return (
    <ActionIcon
      className={cn("ui-theme-toggle", className)}
      icon={<Icon />}
      label={option.label}
      onClick={() => setTheme(option.next)}
    />
  );
}
